/**
 * Brick Armory — <brick-anatomy>
 * Opens the section's breakdown image in a full-screen modal <dialog>:
 * ESC / close button / backdrop click to close, focus trapped inside,
 * focus returned to the opener, tap/click the image to zoom to the tapped spot.
 */
(() => {
  if (customElements.get('brick-anatomy')) return;

  const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  class BrickAnatomy extends HTMLElement {
    connectedCallback() {
      this.opener = this.querySelector('[data-anatomy-open]');
      this.dialog = this.querySelector('[data-anatomy-dialog]');
      if (!this.opener || !this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.zoomButton = this.dialog.querySelector('[data-anatomy-zoom]');
      this.scroller = this.dialog.querySelector('[data-anatomy-scroller]');

      this.opener.addEventListener('click', () => this.open());
      this.dialog.querySelectorAll('[data-anatomy-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.zoomButton?.addEventListener('click', (event) => this.toggleZoom(event));
    }

    open() {
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.dialog.querySelector('[data-anatomy-close]')?.focus();
    }

    close() {
      if (!this.dialog.open) return;
      this.setZoom(false);
      this.dialog.close();
      document.documentElement.style.overflow = '';
      this.opener.focus();
    }

    setZoom(on) {
      this.dialog.classList.toggle('is-zoomed', on);
      this.zoomButton?.setAttribute('aria-pressed', String(on));
    }

    toggleZoom(event) {
      const zoomed = this.zoomButton.getAttribute('aria-pressed') === 'true';
      const img = this.zoomButton.querySelector('img');
      if (zoomed || !img) {
        this.setZoom(false);
        return;
      }
      // Keep the tapped point under the finger (keyboard: zoom to centre)
      const rect = img.getBoundingClientRect();
      const fromPointer = event.detail > 0 && rect.width > 0;
      const rx = fromPointer ? Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)) : 0.5;
      const ry = fromPointer ? Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)) : 0.5;
      this.setZoom(true);
      requestAnimationFrame(() => {
        const z = img.getBoundingClientRect();
        this.scroller.scrollLeft = rx * z.width - this.scroller.clientWidth / 2;
        this.scroller.scrollTop = ry * z.height - this.scroller.clientHeight / 2;
      });
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('brick-anatomy', BrickAnatomy);
})();
